import TokenLogo from '@/src/components/AppComponents/TokenLogoWithCircle/TokenLogoWithCircle';
import { Button } from '@/src/components/Commons/Button';
import NumberInput from '@/src/components/Commons/Controllers/NumberInput/NumberInput';
import LoaderButton from '@/src/components/Commons/LoaderButton/LoaderButton';
import { Decimals, isTokenADA, LucidToolsFrontEnd } from '@/src/lib/SmartDB';
import { useWalletStore } from '@/src/store/global';
import { formatAmountWithUnit, formatTicker } from '@/src/lib/SmartDB/Commons/formatters';
import { pushSucessNotification, pushWarningNotification } from '@/src/lib/SmartDB/Commons/pushNotification';
import { TOKEN_ADA_TICKER } from '@/src/lib/SmartDB/Commons/Constants/constants';
import { explainErrorTx } from '@/src/lib/SmartDB/Commons/explainError';
import { Assets, TxBuilder } from '@lucid-evolution/lucid';
import { useEffect, useMemo, useState } from 'react';
import styles from './TokenInputController.module.scss';

interface TokenInputControllerProps {
    CS: string;
    TN_Hex: string;
    ticker?: string;
    image?: string;
    value?: bigint;
    setValue: (value: bigint) => void;
    decimals?: Decimals;
    min?: bigint;
    max?: bigint;
    max_available_forSpend?: bigint;
    isValidInput?: boolean;
    setIsValidInput?: (isValidInput: boolean) => void;
    isTokensStoreReady?: boolean;
}

export default function TokenInputController(props: TokenInputControllerProps) {
    //--------------------------------------
    const { CS, TN_Hex, ticker, image, value, setValue, decimals, min, max, isValidInput, setIsValidInput, isTokensStoreReady } = props;
    const max_available_forSpend = useMemo(() => (props.max_available_forSpend !== undefined ? props.max_available_forSpend : max), [props.max_available_forSpend, max]);
    //--------------------------------------
    const token = useMemo(
        () => ({
            CS,
            TN_Hex,
            ticker,
            image,
            decimals,
        }),
        [CS, TN_Hex, ticker, image, decimals]
    );
    //--------------------------------------
    const [isFocus, setIsFocus] = useState<boolean>(false);
    //--------------------------------------
    const walletStore = useWalletStore();
    //--------------------------------------
    const [showADAError, setShowADAError] = useState<boolean>(false);
    const [isProcessingSplitTx, setIsProcessingSplitTx] = useState<boolean>(false);
    //--------------------------------------
    useEffect(() => {
        if (value === undefined || decimals === undefined) {
            if (setIsValidInput !== undefined) setIsValidInput(false);
            return;
        }
        const maxControl = max_available_forSpend !== undefined ? max_available_forSpend : max;
        if (maxControl !== undefined && min !== undefined && (value > maxControl || value < min)) {
            if (setIsValidInput !== undefined) setIsValidInput(false);
        } else {
            if (setIsValidInput !== undefined) setIsValidInput(true);
        }
        if (
            isTokenADA(CS, TN_Hex) &&
            walletStore.isWalletDataLoaded &&
            max_available_forSpend !== undefined &&
            max !== undefined &&
            value > max_available_forSpend &&
            value <= max
        ) {
            setShowADAError(true);
        } else {
            setShowADAError(false);
        }
    }, [value, min, max, max_available_forSpend, walletStore.isWalletDataLoaded]);
    //--------------------------------------
    const handleFocus = () => {
        setIsFocus(true);
    };
    const handleBlur = () => {
        setIsFocus(false);
    };
    const handleMin = () => {
        setValue(min ?? 0n);
    };
    const handleMax = () => {
        setValue(max ?? 0n);
    };
    const handleBtnSplitUTXO = async () => {
        //---------------------
        try {
            if (max_available_forSpend !== undefined) {
                if (
                    confirm(
                        `This transaction will consolidate all your available ${TOKEN_ADA_TICKER} into a single UTxO and create an additional UTxO for collateral. Do you wish to proceed?`
                    )
                ) {
                    //--------------------------------------
                    setIsProcessingSplitTx(true);
                    //--------------------------------------
                    const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
                    //--------------------------------------
                    let tx: TxBuilder = lucid.newTx();
                    //---------------------
                    const collateralAmmount = 5500000n;
                    //--------------------------------------
                    if (max_available_forSpend > collateralAmmount) {
                        const value_For_CollateralUTxO: Assets = { ['lovelace']: collateralAmmount };
                        const value_For_FreeADAUTxO: Assets = { ['lovelace']: max_available_forSpend - collateralAmmount };
                        //---------------------
                        tx = tx.pay
                            .ToAddress(walletTxParams.address, value_For_CollateralUTxO)
                            .pay.ToAddress(walletTxParams.address, value_For_FreeADAUTxO)
                            .addSigner(walletTxParams.address);
                    } else if (max_available_forSpend > 0n) {
                        //---------------------
                        const value_For_FreeADAUTxO: Assets = { ['lovelace']: max_available_forSpend };
                        //---------------------
                        tx = tx.pay.ToAddress(walletTxParams.address, value_For_FreeADAUTxO).addSigner(walletTxParams.address);
                    } else {
                        throw `Not enough ${TOKEN_ADA_TICKER} to build transaction (including fee, collateral UTxO, change outputs, token fragmentation, etc.).`;
                    }
                    //--------------------------------------
                    const txComplete = await tx.complete();
                    //--------------------------------------
                    const txCborHex = txComplete.toCBOR({ canonical: true });
                    //--------------------------------------
                    const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
                    //--------------------------------------
                    if (await LucidToolsFrontEnd.awaitTxSimple(lucid, txHash)) {
                        console.log(`handleBtnSplitUTXO - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                        pushSucessNotification(`Collateral UTxO`, txHash, true);
                    } else {
                        console.log(`handleBtnSplitUTXO - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                        throw `Tx not confirmed`;
                    }
                    //--------------------------------------
                    setIsProcessingSplitTx(false);
                    //--------------------------------------
                    await walletStore.loadWalletData();
                    //--------------------------------------
                    return true;
                    //--------------------------------------
                }
            }
        } catch (error) {
            //--------------------------------------
            setIsProcessingSplitTx(false);
            //------------------------------------
            const error_explained = explainErrorTx(error);
            console.log(`handleBtnSplitUTXO - Error: ${error_explained}`);
            pushWarningNotification(`Collateral UTxO`, error_explained);
            return false;
        }
    };
    //--------------------------------------
    return (
        <>
            <section className={styles.inputSection}>
                <div className={`${styles.inputGroup} ${isFocus ? styles.focus : ''}`}>
                    <div className={styles.token}>
                        {isTokensStoreReady === false ? (
                            <div className={styles.tokenLogo}>
                                <LoaderButton />
                            </div>
                        ) : (
                            <TokenLogo token={token} showBorder={false} />
                        )}
                        <h3 className={styles.tokenName}>{formatTicker(token.ticker)}</h3>
                    </div>
                    <NumberInput
                        decimals={decimals}
                        className={`${styles.input} ${isValidInput === false ? styles.error : ''}`}
                        value={value}
                        setValue={setValue}
                        handleFocus={handleFocus}
                        handleBlur={handleBlur}
                    />

                    {max_available_forSpend !== undefined && (
                        <div className={styles.balance}>
                            <p className={styles.title}>Balance:</p>
                            <p className={styles.value} onClick={handleMax}>
                                {formatAmountWithUnit(max_available_forSpend, '', 2, true, decimals)}
                            </p>
                        </div>
                    )}

                    {/* <div className={styles.btnGroup}>
                        <Button mode="action" width={55} onClick={handleMin}>
                            MIN
                        </Button>
                        <Button mode="action" width={55} onClick={handleMax}>
                            MAX
                        </Button>
                    </div> */}
                </div>
                {(showADAError || isProcessingSplitTx) && (
                    <>
                        <div className={styles.ADAError}>
                            <p className={styles.title}>
                                <>Not enough {TOKEN_ADA_TICKER} to build transaction (including fee, collateral UTxO, change outputs, token fragmentation, etc.). You can try:</>
                            </p>
                            <div className={styles.btn}>
                                <Button mode="action" onClick={handleBtnSplitUTXO} disabled={isProcessingSplitTx}>
                                    Arrange UTxOs
                                    {isProcessingSplitTx && <LoaderButton />}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </>
    );
}
